import { useMemo } from 'react'

/**
 * Every game image from jordandraper.com, used as a decorative
 * scrolling collage behind the landing hero.
 */
const GAMES = [
  '/games/ideatthat.webp',
  '/games/colorful.webp',
  '/games/jidohanbaiki.webp',
  '/games/cactus.webp',
  '/games/ceramic.webp',
  '/games/metal.webp',
  '/games/capsule-hotel.webp',
  '/games/tsukiji-market.webp',
  '/games/tokyo-metro.webp',
  '/games/tokyo-game-show.webp',
  '/games/tokyo-jutaku.webp',
  '/games/praise.webp',
  '/games/coin-laundry.webp',
  '/games/turin-market.webp',
  '/games/mirror.webp',
  '/games/washi-cops.webp',
  '/games/tsukiji-exp.webp',
  '/games/currency-sakana.webp',
]

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

interface ColumnProps {
  images: string[]
  direction: 'up' | 'down'
  duration: number
}

function Column({ images, direction, duration }: ColumnProps) {
  // Double the list so the translate -50% loop is seamless.
  const doubled = [...images, ...images]
  return (
    <div className="flex-1 min-w-0">
      <div
        className="flex flex-col gap-3 will-change-transform"
        style={{
          animation: `${direction === 'up' ? 'scrollUp' : 'scrollDown'} ${duration}s linear infinite`,
        }}
      >
        {doubled.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="w-full aspect-square rounded-2xl object-cover shadow-md"
            style={{ transform: `rotate(${((i * 7) % 9) - 4}deg)` }}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Three slowly-scrolling vertical columns of JDG box art, used as a
 * decorative background on the landing screen. Columns alternate
 * direction and run at slightly different speeds for a dynamic feel.
 */
export function FloatingGames() {
  const { a, b, c } = useMemo(() => ({
    a: shuffle(GAMES).slice(0, 8),
    b: shuffle(GAMES).slice(0, 8),
    c: shuffle(GAMES).slice(0, 8),
  }), [])

  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none select-none"
      aria-hidden="true"
    >
      {/* The three scrolling columns */}
      <div className="absolute inset-0 flex gap-3 px-3 opacity-55">
        <Column images={a} direction="up"   duration={55} />
        <Column images={b} direction="down" duration={70} />
        <Column images={c} direction="up"   duration={45} />
      </div>

      {/* Soft edge vignette — narrow fades at top & bottom only so the
          collage reads as continuous motion behind the content. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, #FAFAF8 0%, rgba(250,250,248,0) 12%, rgba(250,250,248,0) 88%, #FAFAF8 100%)',
        }}
      />

      {/* Radial spotlight — brightens the area behind the logo so the
          wordmark and subtext stay punchy without hiding the collage. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 35% at 50% 38%, rgba(250,250,248,0.78) 0%, rgba(250,250,248,0.35) 55%, rgba(250,250,248,0) 85%)',
        }}
      />
    </div>
  )
}
